import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import portfolio1 from "@/assets/portfolio-1.jpg";
import portfolio2 from "@/assets/portfolio-2.jpg";
import portfolio3 from "@/assets/portfolio-3.jpg";

const projects = [
  {
    id: "modern-retreat",
    image: portfolio1,
    titleKey: "portfolio.project1Title",
    locationKey: "portfolio.project1Location",
    descriptionKey: "portfolio.project1Desc",
  },
  {
    id: "olive-grove-estate",
    image: portfolio2,
    titleKey: "portfolio.project2Title",
    locationKey: "portfolio.project2Location",
    descriptionKey: "portfolio.project2Desc",
  },
  {
    id: "desert-evening",
    image: portfolio3,
    titleKey: "portfolio.project3Title",
    locationKey: "portfolio.project3Location",
    descriptionKey: "portfolio.project3Desc",
  },
];

const Portfolio = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [activeProject, setActiveProject] = useState<string | null>(null);

  const handleProjectClick = (projectId: string) => {
    // On mobile/tablet: first tap reveals color, second tap navigates
    if (isMobile) {
      if (activeProject === projectId) {
        // Second tap - navigate
        navigate(`/portfolio/${projectId}`);
      } else {
        // First tap - reveal color
        setActiveProject(projectId);
      }
    } else {
      // Desktop - immediate navigation
      navigate(`/portfolio/${projectId}`);
    }
  };

  return (
    <section id="portfolio" className="py-section-lg bg-background">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 lg:gap-6 mb-8 lg:mb-16">
          <div className="max-w-2xl">
            <p className="text-label text-muted-foreground mb-2 lg:mb-4">{t('portfolio.label')}</p>
            <h2 className="text-display-md font-serif text-foreground">
              {t('portfolio.headline')}
            </h2>
          </div>
          <Button variant="hero-outline" size="lg" onClick={() => navigate("/portfolio")}>
            {t('portfolio.viewAll')}
          </Button>
        </div>

        {/* Mobile instruction */}
        <p className="lg:hidden text-sm text-muted-foreground text-center mb-6">
          Tap once to see, twice to enter
        </p>

        {/* Projects Grid - Each project links to its detail page */}
        <div className="grid md:grid-cols-3 gap-6">
          {projects.map((project) => {
            const isActive = activeProject === project.id;
            
            return (
              <div
                key={project.id}
                className="group cursor-interactive"
                onClick={() => handleProjectClick(project.id)}
              >
                {/* Image */}
                <div className="relative overflow-hidden aspect-[3/4] mb-3 lg:mb-6">
                  <img
                    src={project.image}
                    alt={t(project.titleKey)}
                    className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${
                      isActive ? 'grayscale-0' : 'grayscale group-hover:grayscale-0'
                    }`}
                  />
                  <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors duration-500" />
                </div>

                {/* Content */}
                <div>
                  <p className="text-label text-muted-foreground mb-2">{t(project.locationKey)}</p>
                  <h3 className="text-display-sm font-serif text-foreground mb-2 group-hover:text-muted-foreground transition-colors whitespace-pre-line">
                    {t(project.titleKey)}
                  </h3>
                  <p className="text-body text-muted-foreground text-sm">
                    {t(project.descriptionKey)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
